// src/pages/sitegroup/SiteGroupEditPage.js
import React, { useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Box, 
  Typography, 
  Alert,
  Breadcrumbs,
  Link,
  Button,
  Snackbar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getSiteGroup, updateSiteGroup } from '../../api/siteGroupAPI';
import { getCompany } from '../../api/companyAPI';
import SiteGroupForm from '../../components/sitegroup/SiteGroupForm';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';

const SiteGroupEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const siteGroupId = parseInt(id);

  // Fetch site group details
  const { data: siteGroup, isLoading: isLoadingSiteGroup, isError, error } = useQuery({
    queryKey: ['site-group', siteGroupId],
    queryFn: () => getSiteGroup(siteGroupId),
    enabled: !!siteGroupId && !isNaN(siteGroupId)
  });

  // Fetch company details once we have the site group
  const { data: company, isLoading: isLoadingCompany } = useQuery({
    queryKey: ['company', siteGroup?.SiteCompanyId],
    queryFn: () => getCompany(siteGroup.SiteCompanyId),
    enabled: !!siteGroup?.SiteCompanyId
  });

  // Update site group mutation
  const mutation = useMutation({
    mutationFn: (data) => updateSiteGroup(siteGroupId, data),
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['site-group', siteGroupId] });
      queryClient.invalidateQueries({ queryKey: ['site-groups', siteGroup?.SiteCompanyId] });
      
      setSnackbar({
        open: true,
        message: 'Site group updated successfully!',
        severity: 'success'
      });
      
      // Navigate back to site groups list after a brief delay
      setTimeout(() => {
        navigate('/site-groups');
      }, 1500);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error updating site group: ${error.message}`,
        severity: 'error'
      });
    }
  });

  const handleSubmit = async (data) => {
    try {
      await mutation.mutateAsync(data);
    } catch (err) {
      // Error is handled by the mutation
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoadingSiteGroup || isLoadingCompany) {
    return <LoadingSpinner />;
  }

  if (isError || !siteGroup) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Error loading site group'} />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          {/* <Breadcrumbs aria-label="breadcrumb" mb={1}>
            <Link component={RouterLink} to="/" color="inherit">
              Dashboard
            </Link>
            <Link component={RouterLink} to="/site-groups" color="inherit">
              Site Groups
            </Link>
            <Typography color="textPrimary">Edit {siteGroup.SiteGroupName}</Typography>
          </Breadcrumbs> */}
          <Typography variant="h4">Edit Site Group</Typography>
        </div>
        
        <Button 
          component={RouterLink} 
          to="/site-groups" 
          startIcon={<ArrowBackIcon />}
        >
          Back to Site Groups
        </Button>
      </Box>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {mutation.error.message}
        </Alert>
      )}

      <SiteGroupForm 
        siteGroup={siteGroup}
        companyId={siteGroup.SiteCompanyId}
        onSubmit={handleSubmit} 
        isLoading={mutation.isPending}
      />

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
export default SiteGroupEditPage;